import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { PipesModule } from '../../pipes/pipes.module';
import { MatButtonModule } from '@angular/material/button';
import { ThreeDotsActionsEnum } from '../../enums/threeDotsActions.enum';
import { AuthService } from '../../services/auth/auth.service';
import { RoleEnum } from '../../enums/role.enum';
import { PublishStatusEnum } from '../../enums/publishStatus.enum';
import { Category } from '../../models/category';
import { Course } from '../../models/course';

/** Menu sections, rendered in this order and separated by dividers. */
type OptionGroup = 'manage' | 'copy' | 'danger';
const GROUP_ORDER: OptionGroup[] = ['manage', 'copy', 'danger'];

export interface OptionThreeDots {
  name: string;
  icon: string;
  action: ThreeDotsActionsEnum;
  show: boolean;
  group: OptionGroup;
}
@Component({
  selector: 'app-three-dots',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatMenuModule, PipesModule, MatButtonModule],
  templateUrl: './three-dots.component.html',
  styleUrl: './three-dots.component.scss'
})
export class ThreeDotsComponent implements OnInit, OnChanges {

	@Input() model: Category | Course;
	@Input() isCourse = false;
	@Input() manageClients = false;
	@Input() isAssignedByAdmin = false;
	@Output() action = new EventEmitter<ThreeDotsActionsEnum>();

	user = this.authService.auth.user;
	options: OptionThreeDots[] = [];
	/** Visible options split into non-empty sections, in GROUP_ORDER. */
	visibleGroups: OptionThreeDots[][] = [];
	threeDotsActionsEnum = ThreeDotsActionsEnum;

	constructor(
		private authService: AuthService,
		private cdRef: ChangeDetectorRef
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['model'] || changes['isAssignedByAdmin']) {
			this.initializeOptions();
			this.cdRef.detectChanges();
		}
	}

	ngOnInit(): void {
	  this.initializeOptions();
	}

	initializeOptions(): void {
		const isPublished = this.model.status === PublishStatusEnum.PUBLISHED;
		this.options = [
		  { name: 'general.edit', icon: 'pencil_simple', action: ThreeDotsActionsEnum.EDIT, show: this.canEditOrDelete(), group: 'manage' },
		  { name: 'general.add_translation', icon: 'world_black', action: ThreeDotsActionsEnum.ADD_TRANSLATIONS, show: this.canAddTranslation(isPublished), group: 'manage' },
		  { name: 'general.manage_clients', icon: 'manage_users', action: ThreeDotsActionsEnum.MANAGE_CLIENTS, show: this.canManageClients(isPublished), group: 'manage' },
		  { name: 'general.change_quiz_percentage', icon: 'quiz_interrogation', action: ThreeDotsActionsEnum.CHANGE_QUIZ_PERCENTAGE, show: !this.isCourse, group: 'manage' },
		  { name: 'general.duplicate', icon: 'copy', action: ThreeDotsActionsEnum.DUPLICATE, show: true, group: 'copy' },
		  { name: 'general.publish', icon: 'check', action: ThreeDotsActionsEnum.PUBLISH, show: this.canPublished(isPublished), group: 'copy' },
		  { name: 'general.delete', icon: 'delete', action: ThreeDotsActionsEnum.DELETE, show: this.canEditOrDelete(), group: 'danger' },
		];
		this.visibleGroups = GROUP_ORDER
		  .map(group => this.options.filter(option => option.show && option.group === group))
		  .filter(group => group.length > 0);
	  }

	private canPublished(isPublished: boolean): boolean {
		return this.model.readyToPublish && !isPublished;
	}

	private canEditOrDelete(): boolean {
		const canEditOrDeleteBase = !this.model.assigned || this.model.status === PublishStatusEnum.DRAFT;
		return this.isCourse ? canEditOrDeleteBase :  canEditOrDeleteBase && !this.isAssignedByAdmin;
	}

	private canAddTranslation(isPublished: boolean): boolean {
		const canAddTranslationBase = isPublished && this.model.assigned;
	  	return this.isCourse ? canAddTranslationBase : !this.isAssignedByAdmin && canAddTranslationBase;
	}

	private canManageClients(isPublished: boolean): boolean {
	  return this.manageClients && this.user?.role === RoleEnum.ADMIN && isPublished;
	}

	performAction(option: OptionThreeDots): void {
	  this.action.emit(option.action);
	}
  }
